import { Bell, Check, CircleCheck, Clock, Lock, Sparkles, TriangleAlert, X } from 'lucide-react';

import { cn } from '@/design-system/lib/cn';
import { Badge } from '@/design-system/ui/Badge';
import { Button } from '@/design-system/ui/Button';
import { SimpleSelect } from '@/design-system/ui/Select';
import { relativeTime } from '@escala/domain/dates';
import { shortName } from '@escala/domain/palette';
import {
  type AttendanceSummary,
  type Decline,
  type PresenceState,
  type PresenceSummary,
  PRESENCE_LABEL,
  attendanceLabel,
} from '@escala/domain/presence';
import type { Attendance, Member } from '@escala/domain/types';

/**
 * As peças de presença do painel do evento: o resumo das confirmações com "Cobrar
 * pendentes", o check-in do dia, a marcação de cada pessoa e a "Vaga reaberta" de quem
 * avisou que não pode ir. As regras estão em `@escala/domain/presence`.
 */

export function PresenceBar({
  summary,
  canNudge,
  nudgeScope,
  nudgedAt,
  cooling,
  onNudge,
}: {
  summary: PresenceSummary;
  canNudge: boolean;
  /** "Só as suas funções" quando a cobrança vai para parte dos pendentes. */
  nudgeScope: string;
  nudgedAt: number | null;
  cooling: boolean;
  onNudge: () => void;
}) {
  const items = [
    { n: summary.confirmed, label: summary.confirmed === 1 ? 'confirmado' : 'confirmados', icon: Check },
    { n: summary.pending, label: summary.pending === 1 ? 'pendente' : 'pendentes', icon: Clock },
    { n: summary.declined, label: summary.declined === 1 ? 'recusa' : 'recusas', icon: X },
  ];

  return (
    <div
      aria-label="Confirmações de presença"
      className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-block border border-line-card bg-surface-subtle px-3.5 py-2.5"
    >
      <span className="text-meta font-semibold text-ink-subtle">Presença</span>
      {items.map((item) => (
        <span key={item.label} className="inline-flex items-center gap-1 text-small text-ink-secondary">
          <item.icon aria-hidden className="size-3.5 stroke-2" />
          <span data-numeric className="font-bold text-ink">
            {item.n}
          </span>{' '}
          {item.label}
        </span>
      ))}
      <span className="flex-1" />
      {canNudge ? (
        <span className="flex flex-wrap items-center gap-2">
          {nudgeScope ? (
            <span
              title="A cobrança vai apenas para os pendentes da sua área"
              className="text-caption text-ink-subtle"
            >
              {nudgeScope}
            </span>
          ) : null}
          {nudgedAt ? (
            <span className="text-caption text-ink-subtle">Cobrado {relativeTime(nudgedAt)}</span>
          ) : null}
          <Button size="xs" variant="soft" onClick={onNudge} disabled={summary.pending === 0 || cooling}>
            <Bell aria-hidden />
            Cobrar pendentes
          </Button>
        </span>
      ) : null}
    </div>
  );
}

export function CheckInPanel({
  editing,
  summary,
  scopeNote,
  onStart,
  onAllPresent,
  onDone,
  onCancel,
}: {
  editing: boolean;
  summary: AttendanceSummary;
  scopeNote: string;
  onStart: () => void;
  onAllPresent: () => void;
  onDone: () => void;
  onCancel: () => void;
}) {
  const marked = summary.present + summary.absent > 0;

  return (
    <section
      aria-label="Check-in do dia"
      className="flex flex-col gap-2 rounded-block border border-line-card px-3.5 py-3"
    >
      <div className="flex flex-wrap items-center gap-2">
        <CircleCheck aria-hidden className="size-4 stroke-[1.8] text-status-complete" />
        <h3 className="m-0 flex-1 text-control font-bold">Check-in do dia</h3>
        {editing ? (
          <>
            <Button size="xs" variant="ghost" onClick={onCancel}>
              Cancelar
            </Button>
            <Button size="xs" variant="secondary" onClick={onAllPresent}>
              Todos presentes
            </Button>
            <Button size="xs" onClick={onDone}>
              <Check aria-hidden />
              Concluir
            </Button>
          </>
        ) : (
          <Button size="xs" onClick={onStart}>
            {marked ? 'Editar check-in' : 'Fazer check-in'}
          </Button>
        )}
      </div>
      {marked ? (
        <p className="m-0 flex flex-wrap gap-x-3 text-small text-ink-secondary">
          <span>
            <b data-numeric>{summary.present}</b> {summary.present === 1 ? 'presente' : 'presentes'}
          </span>
          <span>
            <b data-numeric>{summary.absent}</b> {summary.absent === 1 ? 'falta' : 'faltas'}
          </span>
          <span>
            <b data-numeric>{summary.substituted}</b> com substituto
          </span>
          {summary.unmarked ? (
            <span>
              <b data-numeric>{summary.unmarked}</b> sem marcação
            </span>
          ) : null}
        </p>
      ) : null}
      {editing ? (
        <p className="m-0 text-meta text-ink-subtle">
          Marque quem compareceu. Em caso de falta, informe quem serviu no lugar.
        </p>
      ) : null}
      {scopeNote ? (
        <p className="m-0 flex items-center gap-1.5 text-meta text-ink-subtle">
          <Lock aria-hidden className="size-3.5" />
          {scopeNote}
        </p>
      ) : null}
      {!marked && !editing ? (
        <p className="m-0 text-meta text-ink-subtle">
          O check-in alimenta o histórico real e a frequência de cada membro.
        </p>
      ) : null}
    </section>
  );
}

const NO_ONE = 'ninguem';

/** Presente / Faltou de uma pessoa no check-in, com "quem serviu no lugar". */
export function CheckInToggle({
  name,
  mark,
  substitutes,
  onChange,
}: {
  name: string;
  mark: Attendance | undefined;
  substitutes: readonly Member[];
  onChange: (mark: Attendance) => void;
}) {
  const absent = mark?.status === 'absent';

  return (
    <div className="flex flex-col gap-1.5">
      <span className="inline-flex overflow-hidden rounded-control-sm border border-line-input">
        <button
          type="button"
          aria-pressed={mark?.status === 'present'}
          aria-label={`${name} presente`}
          onClick={() => onChange({ status: 'present', substituteId: null })}
          className={cn(
            'h-8 px-2.5 text-meta font-semibold',
            mark?.status === 'present'
              ? 'bg-status-complete-bg text-status-complete'
              : 'bg-white text-ink-secondary'
          )}
        >
          Presente
        </button>
        <button
          type="button"
          aria-pressed={absent}
          aria-label={`${name} faltou`}
          onClick={() => onChange({ status: 'absent', substituteId: mark?.substituteId ?? null })}
          className={cn(
            'h-8 border-l border-line-input px-2.5 text-meta font-semibold',
            absent ? 'bg-status-conflict-bg text-status-conflict' : 'bg-white text-ink-secondary'
          )}
        >
          Faltou
        </button>
      </span>
      {absent ? (
        <SimpleSelect
          size="sm"
          aria-label={`Quem serviu no lugar de ${name}`}
          value={mark.substituteId ?? NO_ONE}
          onValueChange={(value) =>
            onChange({ status: 'absent', substituteId: value === NO_ONE ? null : value })
          }
          options={[
            { value: NO_ONE, label: 'Ninguém no lugar', pin: true },
            ...substitutes.map((m) => ({ value: m.id, label: m.name })),
          ]}
        />
      ) : null}
    </div>
  );
}

export function AttendanceBadge({ mark, substitute }: { mark: Attendance; substitute: string }) {
  return (
    <Badge size="sm" tone={mark.status === 'present' ? 'complete' : 'conflict'}>
      {mark.status === 'present' ? <Check aria-hidden /> : <X aria-hidden />}
      {attendanceLabel(mark, substitute)}
    </Badge>
  );
}

const RESPONSE_TONE: Record<PresenceState, 'complete' | 'partial' | 'conflict'> = {
  confirmed: 'complete',
  pending: 'partial',
  declined: 'conflict',
};

export function ResponseBadge({ state, at }: { state: PresenceState; at: number | null }) {
  const Icon = state === 'confirmed' ? Check : Clock;

  return (
    <Badge
      size="sm"
      tone={RESPONSE_TONE[state]}
      title={at ? `${PRESENCE_LABEL[state]} ${relativeTime(at)}` : PRESENCE_LABEL[state]}
    >
      <Icon aria-hidden />
      {state === 'confirmed' ? 'Confirmado' : 'Pendente'}
    </Badge>
  );
}

/** "Vaga reaberta": quem avisou que não pode ir, com o motivo e o substituto sugerido. */
export function DeclineRow({
  decline,
  name,
  canEdit,
  suggestion,
  onFill,
  onPick,
}: {
  decline: Decline;
  name: string;
  canEdit: boolean;
  suggestion: Member | null;
  onFill: (memberId: string) => void;
  onPick: () => void;
}) {
  return (
    <div className="flex flex-col gap-2 border-t border-line-divider bg-warning-soft px-3 py-2.5">
      <div className="flex items-start gap-2">
        <TriangleAlert aria-hidden className="mt-0.5 size-4 shrink-0 stroke-[1.8] text-status-partial" />
        <div className="min-w-0 text-small text-ink-secondary">
          <span className="font-semibold text-warning-deep">Vaga reaberta</span> · {name} não pode ir
          <div className="text-caption text-ink-subtle">
            {decline.reason ? `${decline.reason} · ` : ''}
            {relativeTime(decline.at)}
          </div>
        </div>
      </div>
      {canEdit ? (
        <div className="flex flex-wrap items-center gap-1.5 pl-6">
          {suggestion ? (
            <Button
              size="xs"
              variant="soft"
              title="Habilitado, disponível, sem ausência e o menos escalado no mês"
              onClick={() => onFill(suggestion.id)}
            >
              <Sparkles aria-hidden />
              Escalar {shortName(suggestion.name)}
            </Button>
          ) : null}
          <Button size="xs" variant="secondary" onClick={onPick}>
            Escolher outro
          </Button>
          {!suggestion ? (
            <span className="text-caption text-ink-subtle">Nenhum substituto disponível no momento.</span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
